/**
 * ModuleCatalogEditModal
 * Modal för att redigera en katalogpost (syfte/funktioner/krav-noteringar) eller skapa en egen post.
 */

import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import type {
  ModuleCatalogEntry,
  CreateModuleCatalogEntryDto,
  UpdateModuleCatalogEntryDto,
} from '@/services/crm/moduleCatalogService';

interface Props {
  entry: ModuleCatalogEntry | null;
  mode: 'edit' | 'create';
  saving?: boolean;
  onClose: () => void;
  onSaveEdit: (id: number, dto: UpdateModuleCatalogEntryDto) => void;
  onSaveCreate: (dto: CreateModuleCatalogEntryDto) => void;
}

const ModuleCatalogEditModal = ({ entry, mode, saving, onClose, onSaveEdit, onSaveCreate }: Props) => {
  const { t } = useTranslation();
  const [systemKey, setSystemKey] = useState('');
  const [systemDisplayName, setSystemDisplayName] = useState('');
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [functions, setFunctions] = useState('');
  const [requirementsNotes, setRequirementsNotes] = useState('');

  useEffect(() => {
    setSystemKey(entry?.system_key ?? '');
    setSystemDisplayName(entry?.system_display_name ?? '');
    setName(entry?.name ?? '');
    setPurpose(entry?.purpose ?? '');
    setFunctions(entry?.functions ?? '');
    setRequirementsNotes(entry?.requirements_notes ?? '');
  }, [entry]);

  const handleSubmit = () => {
    if (mode === 'create') {
      if (!systemKey.trim() || !systemDisplayName.trim() || !name.trim()) return;
      onSaveCreate({
        system_key: systemKey.trim(),
        system_display_name: systemDisplayName.trim(),
        name: name.trim(),
        purpose: purpose.trim() || null,
        functions: functions.trim() || null,
        requirements_notes: requirementsNotes.trim() || null,
      });
    } else if (entry) {
      onSaveEdit(entry.id, {
        system_display_name: systemDisplayName.trim(),
        name: name.trim(),
        purpose: purpose.trim() || null,
        functions: functions.trim() || null,
        requirements_notes: requirementsNotes.trim() || null,
      });
    }
  };

  const labelCls = 'block text-sm font-medium text-textDefault mb-1';
  const inputCls =
    'w-full px-3 py-2 bg-bgContainer border border-border rounded-lg text-textDefault focus:border-primary focus:outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-bgElevated rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-bgElevated">
          <h2 className="text-lg font-semibold text-textDefault">
            {mode === 'create'
              ? t('crm.moduleCatalog.addCustom') || 'Lägg till egen modul'
              : t('crm.moduleCatalog.editEntry') || 'Redigera modul'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-bgContainer rounded">
            <XMarkIcon className="w-5 h-5 text-textSecondary" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {mode === 'create' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>{t('crm.moduleCatalog.systemKey') || 'System (nyckel)'}</label>
                <input className={inputCls} value={systemKey} onChange={(e) => setSystemKey(e.target.value)} placeholder="deque" />
              </div>
              <div>
                <label className={labelCls}>{t('crm.moduleCatalog.systemName') || 'Systemnamn'}</label>
                <input className={inputCls} value={systemDisplayName} onChange={(e) => setSystemDisplayName(e.target.value)} placeholder="Deque" />
              </div>
            </div>
          )}

          {mode === 'edit' && (
            <div>
              <label className={labelCls}>{t('crm.moduleCatalog.systemName') || 'Systemnamn'}</label>
              <input className={inputCls} value={systemDisplayName} onChange={(e) => setSystemDisplayName(e.target.value)} />
            </div>
          )}

          <div>
            <label className={labelCls}>{t('crm.moduleCatalog.name') || 'Modulnamn'}</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label className={labelCls}>{t('crm.moduleCatalog.purpose') || 'Syfte'}</label>
            <textarea className={inputCls} rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
          </div>

          <div>
            <label className={labelCls}>{t('crm.moduleCatalog.functions') || 'Funktioner'}</label>
            <textarea className={inputCls} rows={4} value={functions} onChange={(e) => setFunctions(e.target.value)} />
          </div>

          <div>
            <label className={labelCls}>{t('crm.moduleCatalog.requirementsNotes') || 'Krav-noteringar (Skall/Börkrav)'}</label>
            <textarea className={inputCls} rows={3} value={requirementsNotes} onChange={(e) => setRequirementsNotes(e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-bgElevated">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-textDefault hover:bg-bgContainer">
            {t('common.cancel') || 'Avbryt'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? t('common.saving') || 'Sparar...' : t('common.save') || 'Spara'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleCatalogEditModal;
